import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import MentionInput from './MentionInput';
import { ActionCard } from '../people/MyActionsPage';

// ── Unified comments + actions panel ─────────────────────────────────────────
// One input handles both: plain text → permanent comment; @mention → action that
// notifies tagged people and appears in their Actions (On me) until done. Actions
// are the single person-to-person work primitive, shared with the Actions page.
export default function ActionsPanel({ entityType, entityId, people = [], milestones = [], currentUser, onChanged, initialActionId }) {
  const [actions, setActions]   = useState(null);
  const [filter, setFilter]     = useState('open');

  const activePeople = people.filter(p => !p.employee_status || p.employee_status === 'active');

  const load = useCallback(async () => {
    try {
      const data = await api.get('actions', { entity_type: entityType, entity_id: entityId });
      setActions(Array.isArray(data) ? data : []);
    } catch { setActions([]); }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  // Deep-linked to a specific action (from a notification / Actions page): if it's
  // already resolved, flip to "all" so the row is visible to expand.
  useEffect(() => {
    if (!initialActionId || !Array.isArray(actions)) return;
    const target = actions.find(a => a.id === initialActionId);
    if (target && target.status !== 'open') setFilter('all');
  }, [initialActionId, actions]);

  const refresh = async () => {
    await load();
    onChanged && onChanged();
  };

  const openCount     = (actions || []).filter(a => a.status === 'open').length;
  const resolvedCount = (actions || []).filter(a => a.status !== 'open').length;
  const visible   = (actions || []).filter(a => filter === 'all' || a.status === 'open');

  return (
    <div className="bg-white border border-gray-200 rounded mb-3">
      {/* Unified compose input */}
      <UnifiedInput
        entityType={entityType} entityId={entityId}
        people={people} activePeople={activePeople}
        milestones={milestones}
        onPosted={refresh}
      />

      {/* Actions list — only rendered when there are any */}
      {actions !== null && (actions.length > 0 || filter === 'all') && (
        <>
          <div className="px-3 py-1.5 border-t border-gray-100 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Actions</span>
              {openCount > 0 && (
                <span className="text-[7px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">{openCount} open</span>
              )}
            </div>
            {/* Active shown by default; reveal completed (with a count) on demand. */}
            {resolvedCount > 0 && (
              <button
                onClick={() => setFilter(filter === 'all' ? 'open' : 'all')}
                className="text-[8px] font-semibold text-navy hover:underline">
                {filter === 'all' ? 'Hide completed' : `Show ${resolvedCount} completed`}
              </button>
            )}
          </div>
          <div className="p-2 space-y-1.5">
            {visible.length === 0 && (
              <div className="px-3 py-3 text-[10px] text-gray-400 text-center">
                {filter === 'open' ? 'No open actions.' : 'No actions yet.'}
              </div>
            )}
            {visible.map(a => (
              <ActionCard
                key={a.id} action={a} people={people} currentUser={currentUser}
                onChanged={refresh} showClear={false}
                defaultExpanded={a.id === initialActionId}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Unified input: plain text → comment, @mention → action ──────────────────
function UnifiedInput({ entityType, entityId, people, activePeople, milestones, onPosted }) {
  const [body, setBody]           = useState('');
  const [mentions, setMentions]   = useState([]);
  const [milestoneId, setMilestoneId] = useState('');
  const [completionMode, setCompletionMode] = useState('all');
  const [dueDate, setDueDate]     = useState('');
  const [busy, setBusy]           = useState(false);

  const isAction = mentions.length > 0;
  const multi    = mentions.length > 1;

  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    try {
      if (isAction) {
        const autoTitle = body.trim().slice(0, 80) + (body.trim().length > 80 ? '…' : '');
        await api.post('actions', {
          entity_type: entityType,
          entity_id: entityId,
          title: autoTitle,
          body: body.trim(),
          mentions,
          milestone_id: milestoneId || null,
          due_date: dueDate || null,
          completion_mode: multi ? completionMode : 'all',
        });
      } else {
        await api.post('commentary', {
          entity_type: entityType,
          entity_id: entityId,
          content: body.trim(),
        });
      }
      setBody(''); setMentions([]); setMilestoneId(''); setCompletionMode('all'); setDueDate('');
      await onPosted();
    } finally { setBusy(false); }
  };

  const mentionNames = [...new Set(mentions
    .map(id => { const p = people.find(x => x.id === id); return p ? p.first_name : null; })
    .filter(Boolean))];

  return (
    <div className="p-3">
      <MentionInput
        value={body} rows={2} autoFocus={false}
        onChange={(t, ids) => { setBody(t); setMentions(ids); }}
        people={people}
        placeholder="Add a comment… or type @ to tag a colleague and raise an action"
        onSubmit={submit}
      />
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {isAction && milestones.length > 0 && (
          <select className="form-select text-[9px]" value={milestoneId} onChange={e => setMilestoneId(e.target.value)}>
            <option value="">🔗 Link milestone (optional)</option>
            {milestones.map(m => (
              <option key={m.id} value={m.id}>{m.milestone_code}{m.description ? ` — ${m.description.slice(0, 40)}` : ''}</option>
            ))}
          </select>
        )}
        {isAction && (
          <input type="date" className="form-input text-[9px] py-1 w-auto" value={dueDate}
            onChange={e => setDueDate(e.target.value)} title="Due date (optional)" />
        )}
        {/* Multi-assignee completion mode */}
        {multi && (
          <div className="flex items-center border border-gray-200 rounded overflow-hidden" title="When several people are tagged, decide whether all must complete or just the first">
            {[{ id: 'all', l: '✔ All must complete' }, { id: 'any', l: '⚡ First to complete' }].map(o => (
              <button key={o.id} type="button" onClick={() => setCompletionMode(o.id)}
                className={`px-2 py-0.5 text-[8px] font-semibold ${completionMode === o.id ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>{o.l}</button>
            ))}
          </div>
        )}
        {isAction && (
          <span className="text-[8px] text-amber-700 font-semibold">
            🔔 {mentionNames.join(', ')} will be notified
          </span>
        )}
        <button
          onClick={submit} disabled={!body.trim() || busy}
          className={`ml-auto px-3 py-1 text-[10px] font-semibold rounded disabled:opacity-30 transition-colors ${
            isAction ? 'bg-amber-500 text-amber-950 hover:bg-amber-600' : 'bg-navy text-white hover:bg-navy/80'
          }`}
        >
          {busy ? '…' : isAction ? 'Raise action' : 'Post comment'}
        </button>
      </div>
    </div>
  );
}
